import * as THREE from 'three';
import type { InteractionVisualStateContext } from '../../manager/interactionState/AbstractInteractionVisualState';
import type { ImportedModelBodyTreeSelectionFaceHighlighterDeps } from '../../../types';
import { ImportedModelInstancedSelectionBatchManager, ImportedModelSelectionPrewarmer } from '../instancing/selection';
import { ImportedModelBodySelectionOverlayMesh } from './ImportedModelBodySelectionOverlayMesh';
import { ImportedModelSelectionGroup } from './ImportedModelSelectionGroup';
import { ImportedModelTreeSelectionTargetResolver } from './ImportedModelTreeSelectionTargetResolver';

/**
 * 特征树整组件选择显示的生命周期协调器。
 * 复用正式渲染模板并增量维护选择组；不创建拓扑宿主、不重建正式实例、不修改业务材质。
 */
export class ImportedModelBodyTreeSelectionFaceHighlighter {
    /** 普通来源的活动显示模板；重复刷新复用同一个对象。 */
    private readonly overlays = new Map<THREE.Mesh, ImportedModelBodySelectionOverlayMesh>();
    /** 按正式父级分组，组本身保持单位矩阵，不引入额外坐标换算误差。 */
    private readonly groups = new Map<THREE.Object3D, ImportedModelSelectionGroup>();
    /** 普通和实例来源共用的业务范围解析。 */
    private readonly targetResolver: ImportedModelTreeSelectionTargetResolver;
    /** 保持实例槽 Float32 矩阵投影的既有底层协议。 */
    private readonly instancedOverlays: ImportedModelInstancedSelectionBatchManager;
    /** 空闲预热只建立派生缓存；不接管正式实例或选择状态。 */
    private readonly prewarmer: ImportedModelSelectionPrewarmer | null;

    /** @param deps 业务查询及材质模板依赖。 */
    public constructor(private readonly deps: ImportedModelBodyTreeSelectionFaceHighlighterDeps) {
        this.targetResolver = new ImportedModelTreeSelectionTargetResolver(deps);
        this.instancedOverlays = new ImportedModelInstancedSelectionBatchManager(
            deps.resolveInstancedMeshUnit,
            deps.getSelectedBodyMaterial,
            (parent) => this.getSelectionGroup(parent),
            () => this.removeEmptyGroups(),
        );
        this.prewarmer = deps.unitSource && deps.idleRenderScheduler
            ? new ImportedModelSelectionPrewarmer(deps.unitSource, deps.idleRenderScheduler, this.instancedOverlays)
            : null;
    }

    /** @param context 当前交互状态，只消费 feature-tree 的整模型选择。 */
    public apply(context: InteractionVisualStateContext): void {
        if (
            context.selectedInteractionMeta?.source !== 'feature-tree' ||
            context.selectedObjects.length === 0
        ) {
            this.reset();
            return;
        }
        const targets = this.targetResolver.resolve(
            context.selectedObjects,
            context.selectedInteractionMeta.entityHighlightTargets,
        );
        const instanced = new Map<THREE.Mesh, readonly number[] | undefined>();
        const ordinary = new Set<THREE.Mesh>();
        try {
            for (const { source, primitiveIndices } of targets.values()) {
                if (this.deps.resolveInstancedMeshUnit(source)) {
                    instanced.set(source, primitiveIndices);
                    continue;
                }
                // 普通 Mesh 与选择组共享业务父级，借用局部矩阵，移动时无需渲染前扫描。
                if (!source.parent) continue;
                ordinary.add(source);
                const parent = this.getSelectionGroup(source.parent);
                let overlay = this.overlays.get(source);
                if (!overlay) {
                    overlay = new ImportedModelBodySelectionOverlayMesh(
                        source,
                        this.deps.getSelectedBodyMaterial(),
                    );
                    this.overlays.set(source, overlay);
                }
                if (overlay.parent !== parent) parent.add(overlay);
                overlay.syncFromSourceHost(source, primitiveIndices);
            }
            this.instancedOverlays.apply([...instanced.keys()], instanced);
            for (const [source, overlay] of this.overlays) {
                if (!ordinary.has(source)) {
                    overlay.dispose();
                    this.overlays.delete(source);
                }
            }
            this.removeEmptyGroups();
        } catch (error) {
            // 失败不能把半套选择显示和 GPU 绑定留在场景中；业务选择由原交互状态保留。
            this.reset();
            throw error;
        }
    }

    /** 清除显示及矩阵绑定；外部 geometry、正式批次和材质配方不释放。 */
    public reset(): void {
        for (const overlay of this.overlays.values()) overlay.dispose();
        this.overlays.clear();
        this.instancedOverlays.clear();
        this.targetResolver.reset();
        this.removeEmptyGroups();
    }

    /** Viewer/状态销毁时释放稳定批次；普通取消只调用 reset 保留热态缓存。 */
    public dispose(): void {
        this.prewarmer?.dispose();
        this.reset();
        this.instancedOverlays.dispose();
        this.removeEmptyGroups();
    }

    /** @param parent 正式对象父级。 @returns 独立于正式批次的单位变换选择组。 */
    private getSelectionGroup(parent: THREE.Object3D): ImportedModelSelectionGroup {
        let group = this.groups.get(parent);
        if (!group) {
            group = new ImportedModelSelectionGroup();
            parent.add(group);
            this.groups.set(parent, group);
        }
        return group;
    }

    /** 最后一个目标取消后立即移除分组，不把业务父级引用留在缓存中。 */
    private removeEmptyGroups(): void {
        for (const [parent, group] of this.groups) {
            if (group.children.length === 0) {
                group.removeFromParent();
                this.groups.delete(parent);
            } else group.visible = group.children.some(child => child.visible);
        }
    }
}
